import { type ReactNode, useMemo, useCallback } from "react";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { useFieldContext } from "./form-context";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { CircleAlert, Redo2 } from "lucide-react";
import { Button } from "../ui/button";
import { sanitizeHtml } from "@/lib/utils";
import { OverridesButton } from "../overrides-button";

type FormMultiSwitchProps = {
  id: string;
  label?: ReactNode;
  description?: string;
  htmlContent?: string;
  options: { label: string; value: string }[];
  bitmask?: boolean;
  defaultSystemValue?: string;
  isSystemSettings?: boolean;
  openOverridesModal?: () => void;
  domain?: string;
};

export function FormMultiSwitch({
  options,
  id,
  label,
  description,
  htmlContent,
  bitmask,
  defaultSystemValue,
  isSystemSettings = false,
  openOverridesModal,
  domain,
}: FormMultiSwitchProps) {
  const field = useFieldContext<any>();
  const value = field.state.value;
  const sanitizedHtml = htmlContent ? sanitizeHtml(htmlContent) : null;
  //The functions below handle cases where the options are represented by bitmask values.
  const selectedSet = useMemo(() => {
    if (bitmask) {
      const mask = typeof value === "number" ? value : Number(value) || 0;
      return new Set(
        options
          .filter((option) => {
            const bitValue = Number(option.value) || 0;
            return bitValue > 0 && (mask & bitValue) === bitValue;
          })
          .map((option) => option.value)
      );
    }

    if (Array.isArray(value)) {
      return new Set<string>(value as string[]);
    }

    const parts = (typeof value === "string" ? value : "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return new Set(parts);
  }, [bitmask, value, options]);

  const overwrittenLabels = useMemo(() => {
    if (!bitmask) {
      const raw = String(defaultSystemValue ?? "").trim();
      if (!raw) return ["None"] as string[];
      const values = raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const labels = values.map((v) => options.find((o) => o.value === v)?.label ?? v);
      return labels.length > 0 ? labels : ["None"];
    }
    const raw = String(defaultSystemValue ?? "").trim();
    const mask = parseInt(raw, 10);
    if (!Number.isFinite(mask) || mask === 0) return ["None"] as string[];
    return options
      .filter((option) => {
        const bitValue = parseInt(String(option.value), 10);
        if (!Number.isFinite(bitValue) || bitValue <= 0) return false;
        return (mask & bitValue) === bitValue;
      })
      .map((option) => option.label);
  }, [bitmask, defaultSystemValue, options]);

  const currentLabels = useMemo(() => {
    if (bitmask) {
      const mask = typeof value === "number" ? value : Number(value) || 0;
      if (mask === 0) return ["None"];
      return options
        .filter((option) => {
          const bitValue = parseInt(String(option.value), 10);
          return Number.isFinite(bitValue) && bitValue > 0 && (mask & bitValue) === bitValue;
        })
        .map((option) => option.label);
    }
    const parts = Array.isArray(value)
      ? (value as string[])
      : (typeof value === "string" ? value : "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
    if (parts.length === 0) return ["None"];
    return parts.map((v) => options.find((o) => o.value === v)?.label ?? v);
  }, [bitmask, value, options]);

  const onToggle = useCallback(
    (optionValue: string, checked: boolean) => {
      if (bitmask) {
        const currentMask = typeof value === "number" ? value : Number(value) || 0;
        const bitValue = Number(optionValue) || 0;
        const nextMask = checked ? currentMask | bitValue : currentMask & ~bitValue;
        field.handleChange(String(nextMask));
        return;
      }

      if (Array.isArray(value)) {
        const current = new Set<string>(value as string[]);
        if (checked) {
          current.add(optionValue);
        } else {
          current.delete(optionValue);
        }
        field.handleChange(Array.from(current));
        return;
      }

      const current = new Set<string>(selectedSet);
      if (checked) {
        current.add(optionValue);
      } else {
        current.delete(optionValue);
      }
      field.handleChange(Array.from(current).join(","));
    },
    [bitmask, value, selectedSet, field]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        {label && <div className="text-sm leading-none font-medium">{label}</div>}
        {defaultSystemValue !== undefined && value !== defaultSystemValue && (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleAlert className="w-4 h-4 text-orange-500" />
            </TooltipTrigger>
            <TooltipContent className="max-w-64 whitespace-normal wrap-break-word">
              <p>
                Current value ({currentLabels.join(", ")}) overwrites the system default: (
                {overwrittenLabels.join(", ")})
              </p>
            </TooltipContent>
          </Tooltip>
        )}
        {defaultSystemValue !== undefined && value !== defaultSystemValue && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                className="text-orange-500 pb-0 pt-0 mr-auto"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  field?.setValue(defaultSystemValue ?? "");
                }}
              >
                <Redo2 className="w-2 h-2" />
                Reset to default
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-64 whitespace-normal wrap-break-word">
              <p>Reset to system default: {overwrittenLabels.join(", ")}</p>
            </TooltipContent>
          </Tooltip>
        )}
        {isSystemSettings && domain !== "SYSTEM" && <OverridesButton onClick={openOverridesModal!} />}
      </div>
      <div className="flex flex-col gap-2">
        {options.map((option) => {
          const switchId = `${id}-${option.value}`;
          const isChecked = selectedSet.has(option.value);
          return (
            <div key={option.value} className="flex flex-row items-center gap-3">
              <Switch
                id={switchId}
                checked={isChecked}
                onCheckedChange={(checked) => onToggle(option.value, checked)}
              />
              <Label htmlFor={switchId} dangerouslySetInnerHTML={{ __html: sanitizeHtml(option.label) }} />
            </div>
          );
        })}
        {description && <div className="text-xs form-description">{description}</div>}
        {htmlContent && (
          <div className="text-xs form-description" dangerouslySetInnerHTML={{ __html: sanitizedHtml || "" }} />
        )}
      </div>
    </div>
  );
}
